export const INVOICE_STATUS_OPTIONS = {
    WaitingConfirm: 'waiting-confirm',
    Approved: 'approved',
    Paid: 'paid',
    Cancelled: 'cancelled',
    Rejected: 'rejected',
    Done: 'done',
}

export const INVOICE_STATUS = (t: Function) => [
    {
        label: t(INVOICE_STATUS_OPTIONS.WaitingConfirm),
        value: INVOICE_STATUS_OPTIONS.WaitingConfirm,
        color: 'info',
    },
    {
        label: t(INVOICE_STATUS_OPTIONS.Approved),
        value: INVOICE_STATUS_OPTIONS.Approved,
        color: 'primary',
    },
    {
        label: t(INVOICE_STATUS_OPTIONS.Paid),
        value: INVOICE_STATUS_OPTIONS.Paid,
        color: 'success',
    },
    {
        label: t(INVOICE_STATUS_OPTIONS.Cancelled),
        value: INVOICE_STATUS_OPTIONS.Cancelled,
        color: 'error',
    },
    {
        label: t(INVOICE_STATUS_OPTIONS.Rejected),
        value: INVOICE_STATUS_OPTIONS.Rejected,
        color: 'secondary',
    },
    {
        label: t(INVOICE_STATUS_OPTIONS.Done),
        value: INVOICE_STATUS_OPTIONS.Done,
        color: 'success',
    },
]

export const TRANSACTION_PAYMENT_METHODS = (t: Function) => [
    { label: t('bank-transfer'), value: 'bank-transfer', color: 'primary' },
    { label: t('credit'), value: 'credit', color: 'default' },
    { label: t('cash'), value: 'cash', color: 'warning' },
    { label: t('momo'), value: 'momo', color: 'secondary' },
    { label: t('paypal'), value: 'paypal', color: 'secondary' },
]

// Trạng thái chứng từ ký số (payment_request_status / reconciliation_statement_status của hoá đơn)
export const SIGNED_DOCUMENT_STATUS = (t: Function) => [
    {
        label: t('signed-document-status-none', { ns: 'invoice' }),
        value: '',
        color: 'default',
    },
    {
        label: t('signed-document-status-pending', { ns: 'invoice' }),
        value: 'pending',
        color: 'warning',
    },
    {
        label: t('signed-document-status-signed', { ns: 'invoice' }),
        value: 'signed',
        color: 'success',
    },
    {
        label: t('signed-document-status-failed', { ns: 'invoice' }),
        value: 'failed',
        color: 'error',
    },
]

// Bộ lọc danh sách hoá đơn: 'none' = chưa gửi ký
export const SIGNED_DOCUMENT_FILTER_OPTIONS = (t: Function) => [
    {
        label: t('signed-document-status-none', { ns: 'invoice' }),
        value: 'none',
    },
    ...SIGNED_DOCUMENT_STATUS(t).filter((option) => option.value !== ''),
]

export const TRANSACTION_STATUS = (t: Function) => [
    { label: t('transaction-pending'), value: 'pending', color: 'warning' },
    { label: t('completed'), value: 'completed', color: 'success' },
    { label: t('cancelled'), value: 'cancelled', color: 'danger' },
    { label: t('failed'), value: 'failed', color: 'error' },
]

export enum INVOICE_TYPES {
    Default = 'default',
    Deposit = 'deposit',
}

export const INVOICE_ITEM_DESCRIPTIONS = (t: Function) => [
    { label: t('invoices.descriptions.register'), value: 'register' },
    { label: t('invoices.descriptions.commitment'), value: 'commitment' },
    { label: t('invoices.descriptions.renew'), value: 'renew' },
    { label: t('invoices.descriptions.sub'), value: 'sub' },
    {
        label: t('invoices.descriptions.usage_parameters'),
        value: 'usage_parameters',
    },
    { label: t('invoices.descriptions.exceeded'), value: 'exceeded' },
    { label: t('invoices.descriptions.discount'), value: 'discount' },
]

export const UNITS = [{ value: 'gb', label: 'GB' }]

// Hoá đơn DC phải ở các trạng thái này mới phát hành được hoá đơn điện tử (MISA meInvoice)
/**
 * Còn xác nhận được ("Xác nhận hoá đơn" / "Không cần xuất hoá đơn"): chờ xác nhận, hoặc hoá đơn
 * đối soát đã duyệt để ký số nhưng CS chưa chốt số với khách (chưa xác nhận doanh thu).
 * Hoá đơn đã thanh toán thì bỏ qua, không xác nhận lại.
 */
export const isInvoiceConfirmable = (invoice?: {
    invoice_status?: string
    invoice_type?: string
    revenue_confirmed?: boolean | null
}) =>
    invoice?.invoice_status === INVOICE_STATUS_OPTIONS.WaitingConfirm ||
    (invoice?.invoice_status === INVOICE_STATUS_OPTIONS.Approved &&
        !invoice.revenue_confirmed &&
        invoice.invoice_type === INVOICE_TYPES.Default)

/**
 * Quyết định xuất hoá đơn của hoá đơn đã duyệt: 'waiting' = đối soát đã duyệt để ký số, CS chưa
 * chốt với khách; undefined = chưa tới bước này (chờ xác nhận, đã huỷ, hoá đơn ký quỹ).
 */
export const getInvoicableDecision = (invoice?: {
    invoice_status?: string
    invoice_type?: string
    revenue_confirmed?: boolean | null
    invoicable?: boolean | null
}) => {
    if (
        invoice?.invoice_type !== INVOICE_TYPES.Default ||
        !E_INVOICE_PUBLISHABLE_STATUSES.includes(invoice?.invoice_status ?? '')
    )
        return undefined
    // API bản cũ chưa trả hai cờ này: không đoán
    if (invoice.revenue_confirmed === undefined) return undefined
    if (!invoice.revenue_confirmed) return 'waiting'
    return invoice.invoicable ? 'invoicable' : 'non-invoicable'
}

export const INVOICABLE_DECISIONS = (t: Function) => [
    {
        label: t('invoicable-decision.waiting', { ns: 'invoice' }),
        value: 'waiting',
        color: 'warning',
    },
    {
        label: t('invoicable-decision.invoicable', { ns: 'invoice' }),
        value: 'invoicable',
        color: 'success',
    },
    {
        label: t('invoicable-decision.non-invoicable', { ns: 'invoice' }),
        value: 'non-invoicable',
        color: 'default',
    },
]

export const E_INVOICE_PUBLISHABLE_STATUSES: string[] = [
    INVOICE_STATUS_OPTIONS.Approved,
    INVOICE_STATUS_OPTIONS.Paid,
    INVOICE_STATUS_OPTIONS.Done,
]

// Bộ lọc "HĐ điện tử" ở danh sách hoá đơn (e_invoice_status; 'none' = chưa phát hành)
export const E_INVOICE_FILTER_OPTIONS = (t: Function) => [
    { label: t('e-invoice-list-none', { ns: 'invoice' }), value: 'none' },
    {
        label: t('e-invoice-list-publishing', { ns: 'invoice' }),
        value: 'publishing',
    },
    {
        label: t('e-invoice-list-published', { ns: 'invoice' }),
        value: 'published',
    },
    { label: t('e-invoice-list-failed', { ns: 'invoice' }), value: 'failed' },
    { label: t('e-invoice-list-wrong', { ns: 'invoice' }), value: 'wrong' },
]
